import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Edit2, Trash2, ExternalLink, Eye, EyeOff, Star, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEMENT_OPTIONS = [
  { value: "homepage", label: "Homepage" },
  { value: "my_corner", label: "Owner's Perch" },
  { value: "cardio_finish", label: "Cardio Finish" },
  { value: "profile_sidebar", label: "Profile Sidebar" },
  { value: "shop", label: "Shop" },
];

// Aspect-preserving resize → JPEG data URL. Caps the longest side at maxDim.
// Used to keep partner product photos under ~150 KB without forcing a square crop.
async function fileToDataUrl(file, maxDim = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const EMPTY_FORM = {
  partner_id: null, // null = create new
  partner_name: "",
  product_name: "",
  tagline: "",
  roc_note: "",
  image_url: "",
  affiliate_url: "",
  discount_code: "",
  discount_text: "",
  category: "Supplements",
  placements: ["homepage"],
  roc_approved: false,
  active: true,
  sort_order: 999,
};

export default function AdminPartners() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed, object = open in edit/create mode
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const isOwner = user?.role === "owner";

  async function load() {
    setLoading(true);
    try {
      const [{ data: a }, { data: b }] = await Promise.all([
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/admin/partners/insights`),
      ]);
      setPartners(a?.partners || []);
      setInsights(b?.partners || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load partners.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner]);

  function openCreate() {
    setForm({ ...EMPTY_FORM });
  }
  function openEdit(p) {
    setForm({
      partner_id: p.partner_id,
      partner_name: p.partner_name || "",
      product_name: p.product_name || "",
      tagline: p.tagline || "",
      roc_note: p.roc_note || "",
      image_url: p.image_url || "",
      affiliate_url: p.affiliate_url || "",
      discount_code: p.discount_code || "",
      discount_text: p.discount_text || "",
      category: p.category || "Supplements",
      placements: p.placements || [],
      roc_approved: !!p.roc_approved,
      active: p.active !== false,
      sort_order: p.sort_order ?? 999,
    });
  }

  function togglePlacement(v) {
    setForm((f) => ({
      ...f,
      placements: f.placements.includes(v)
        ? f.placements.filter((x) => x !== v)
        : [...f.placements, v],
    }));
  }

  async function save() {
    if (!form.partner_name.trim() || !form.product_name.trim() || !form.affiliate_url.trim()) {
      toast.error("Partner name, product name, and affiliate URL are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // strip null partner_id (create) or use it for update path
      const isUpdate = !!payload.partner_id;
      const updateId = payload.partner_id;
      if (!isUpdate) delete payload.partner_id;
      if (isUpdate) {
        await axios.put(`${API}/admin/partners/${updateId}`, payload);
        toast.success(`Updated ${payload.partner_name}.`);
      } else {
        await axios.post(`${API}/admin/partners`, payload);
        toast.success(`Added ${payload.partner_name}.`);
      }
      setForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(p) {
    if (!window.confirm(`Delete partner "${p.partner_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/partners/${p.partner_id}`);
      toast.success(`Deleted ${p.partner_name}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  async function toggleActive(p) {
    try {
      await axios.put(`${API}/admin/partners/${p.partner_id}`, {
        ...p,
        active: !p.active,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    }
  }

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }
    if (file.size > 8_000_000) {
      toast.error("File too large (max 8 MB before compression).");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file, 800);
      setForm((f) => ({ ...f, image_url: dataUrl }));
      toast.success("Image uploaded · saved when you submit the form.");
    } catch {
      toast.error("Couldn't process that image.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white pt-24 px-4">
        <div className="max-w-3xl mx-auto py-20 text-center">
          <h1 className="font-display text-3xl mb-3">Sign in required</h1>
          <p className="text-neutral-400">This page is owner-only.</p>
        </div>
      </div>
    );
  }
  if (!isOwner) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white pt-24 px-4" data-testid="admin-partners-forbidden">
        <div className="max-w-3xl mx-auto py-20 text-center">
          <h1 className="font-display text-3xl mb-3">Owner Only</h1>
          <p className="text-neutral-400">
            You need the owner role to manage affiliate partners.
          </p>
        </div>
      </div>
    );
  }

  // Insights map — merge into rows
  const insightMap = Object.fromEntries(insights.map((x) => [x.partner_id, x]));

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="admin-partners-page">
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              Owner · Admin
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white">
              Affiliate <span className="text-[#D4AF37]">Partners</span>
            </h1>
            <p className="text-sm text-neutral-400 mt-2 max-w-xl">
              Add, edit, and track every brand that shows up on OmniMuscle. Active partners cycle on the homepage, Owner's Perch, and cardio finish carousels.
            </p>
          </div>
          <button
            onClick={openCreate}
            data-testid="admin-partners-add-btn"
            className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-all flex items-center gap-2"
          >
            <Plus size={14} /> Add Partner
          </button>
        </div>

        {/* Admin section tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-900">
          <span
            data-testid="admin-tab-partners-active"
            className="px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold border-b-2 border-[#D4AF37] text-[#D4AF37]"
          >
            Affiliate Partners
          </span>
          <Link
            to="/admin/supporters"
            data-testid="admin-tab-supporters"
            className="px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold border-b-2 border-transparent text-neutral-500 hover:text-white hover:border-zinc-600 transition-colors"
          >
            Friends &amp; Supporters
          </Link>
        </div>

        {/* Insights summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="admin-partners-insights">
          <Stat label="Total Partners" value={partners.length} />
          <Stat label="Active" value={partners.filter((p) => p.active).length} />
          <Stat
            label="Lifetime Clicks"
            value={insights.reduce((s, x) => s + (x.click_count || 0), 0).toLocaleString()}
          />
          <Stat
            label="Clicks · Last 7d"
            value={insights.reduce((s, x) => s + (x.clicks_7d || 0), 0).toLocaleString()}
            highlight
          />
        </div>

        {/* 👨‍🍳 Partner Egg Drop trigger — Chief Cook David Lauer */}
        <PartnerEggAnnouncePanel />

        {/* 👨‍🍳 Chef bio editor */}
        <ChefBioEditorPanel slug="david-lauer" />

        {/* List */}
        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : partners.length === 0 ? (
          <div className="text-center text-neutral-500 py-12 border border-zinc-900 bg-[#0F0F0F]">
            No partners yet. Click <span className="text-[#D4AF37]">Add Partner</span> to seed your first card.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-partners-list">
            {partners.map((p) => {
              const ins = insightMap[p.partner_id] || {};
              return (
                <div
                  key={p.partner_id}
                  data-testid={`admin-partner-row-${p.partner_id}`}
                  className={
                    "bg-[#0F0F0F] border-2 p-4 flex items-center gap-4 flex-wrap " +
                    (p.active ? "border-zinc-800" : "border-zinc-900 opacity-60")
                  }
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-black border border-zinc-900 overflow-hidden">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.product_name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-700 text-xs">
                        no img
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                        {p.partner_name}
                      </span>
                      {p.roc_approved && (
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] font-bold">
                          ★ Roc Pick
                        </span>
                      )}
                      <span className="text-[9px] uppercase tracking-widest text-neutral-500">
                        {p.category || "—"}
                      </span>
                    </div>
                    <div className="font-display text-lg text-white leading-tight">
                      {p.product_name}
                    </div>
                    {p.tagline && (
                      <div className="text-xs text-neutral-400 mt-0.5 line-clamp-1">
                        {p.tagline}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(p.placements || []).map((pl) => (
                        <span
                          key={pl}
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#160B22] border border-[#4C1D95] text-neutral-300"
                        >
                          {pl}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      Lifetime Clicks
                    </div>
                    <div className="font-mono tabular-nums text-2xl text-white">
                      {(ins.click_count || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#D4AF37] font-mono tabular-nums">
                      +{ins.clicks_7d || 0} · 7d
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => toggleActive(p)}
                      data-testid={`admin-partner-toggle-${p.partner_id}`}
                      title={p.active ? "Click to hide" : "Click to publish"}
                      className={
                        "px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors flex items-center gap-1.5 " +
                        (p.active
                          ? "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                          : "border-zinc-700 text-neutral-400 hover:border-white hover:text-white")
                      }
                    >
                      {p.active ? <Eye size={12} /> : <EyeOff size={12} />}
                      {p.active ? "Live" : "Hidden"}
                    </button>
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`admin-partner-edit-${p.partner_id}`}
                      className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-1.5"
                    >
                      <Edit2 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(p)}
                      data-testid={`admin-partner-delete-${p.partner_id}`}
                      className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 border-red-900 text-red-400 hover:bg-red-700 hover:text-white transition-colors flex items-center gap-1.5"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Form modal */}
      {form && (
        <div
          className="fixed inset-0 bg-black/85 z-[70] flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !saving && setForm(null)}
        >
          <div
            className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-2xl w-full p-6 sm:p-8 my-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-partners-form"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              {form.partner_id ? "Edit Partner" : "New Partner"}
            </div>
            <h2 className="font-display text-3xl text-white mb-5">
              {form.partner_id ? form.partner_name : "Add a brand"}
            </h2>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Field label="Partner Name *">
                <input
                  data-testid="form-partner-name"
                  value={form.partner_name}
                  onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                  placeholder="e.g. Blackstone Labs"
                  className="form-input"
                />
              </Field>
              <Field label="Product Name *">
                <input
                  data-testid="form-product-name"
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  placeholder="e.g. Blackstone Legion"
                  className="form-input"
                />
              </Field>
            </div>

            <Field label="Tagline">
              <input
                data-testid="form-tagline"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="Short product pitch"
                className="form-input"
              />
            </Field>
            <Field label="Roc's Note (italic quote shown on hero card)">
              <textarea
                data-testid="form-roc-note"
                value={form.roc_note}
                onChange={(e) => setForm({ ...form, roc_note: e.target.value })}
                placeholder="Why Roc personally co-signs this brand"
                rows={3}
                className="form-input resize-none"
              />
            </Field>

            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
                Product Image
              </div>
              <div className="flex gap-3 items-start">
                {/* Preview */}
                <div className="flex-shrink-0 w-24 h-24 bg-black border border-zinc-800 overflow-hidden relative">
                  {form.image_url ? (
                    <>
                      <img
                        src={form.image_url}
                        alt="preview"
                        className="w-full h-full object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        data-testid="form-image-clear"
                        title="Clear image"
                        className="absolute top-0 right-0 bg-black/80 text-red-400 hover:text-red-200 p-0.5"
                      >
                        <X size={12} />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-widest text-neutral-700">
                      No img
                    </div>
                  )}
                </div>
                {/* Controls */}
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="form-image-file"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="form-image-upload-btn"
                    className="w-full px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Upload size={12} />
                    {uploading ? "Compressing…" : "Upload Image"}
                  </button>
                  <input
                    data-testid="form-image-url"
                    value={form.image_url?.startsWith("data:") ? "(uploaded image)" : form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    disabled={form.image_url?.startsWith("data:")}
                    placeholder="…or paste an image URL"
                    className="form-input"
                  />
                  <div className="text-[9px] uppercase tracking-widest text-neutral-600">
                    JPG/PNG up to 8 MB · resized to 800px · stored as data URL
                  </div>
                </div>
              </div>
            </div>

            <Field label="Affiliate URL *">
              <input
                data-testid="form-affiliate-url"
                value={form.affiliate_url}
                onChange={(e) => setForm({ ...form, affiliate_url: e.target.value })}
                placeholder="https://brand.com/yourref"
                className="form-input"
              />
            </Field>

            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <Field label="Discount Code">
                <input
                  data-testid="form-discount-code"
                  value={form.discount_code}
                  onChange={(e) => setForm({ ...form, discount_code: e.target.value.toUpperCase() })}
                  placeholder="OMNIMUSCLE"
                  className="form-input"
                />
              </Field>
              <Field label="Discount Text">
                <input
                  data-testid="form-discount-text"
                  value={form.discount_text}
                  onChange={(e) => setForm({ ...form, discount_text: e.target.value })}
                  placeholder="15% off your order"
                  className="form-input"
                />
              </Field>
              <Field label="Category">
                <input
                  data-testid="form-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Supplements"
                  className="form-input"
                />
              </Field>
            </div>

            <Field label="Placements (where this card appears)">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="form-placements">
                {PLACEMENT_OPTIONS.map((opt) => {
                  const on = form.placements.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => togglePlacement(opt.value)}
                      data-testid={`form-placement-${opt.value}`}
                      className={
                        "px-2.5 py-2 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors " +
                        (on
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                          : "border-zinc-800 text-neutral-400 hover:border-zinc-600")
                      }
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <Field label="Sort Order (lower = first)">
                <input
                  type="number"
                  data-testid="form-sort-order"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "999", 10) })}
                  className="form-input"
                />
              </Field>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, roc_approved: !form.roc_approved })}
                  data-testid="form-roc-approved"
                  className={
                    "w-full px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors flex items-center justify-center gap-2 " +
                    (form.roc_approved
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "border-zinc-800 text-neutral-400 hover:border-zinc-600")
                  }
                >
                  <Star size={12} /> Roc Pick
                </button>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, active: !form.active })}
                  data-testid="form-active"
                  className={
                    "w-full px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors flex items-center justify-center gap-2 " +
                    (form.active
                      ? "border-emerald-600 bg-emerald-900/20 text-emerald-400"
                      : "border-zinc-800 text-neutral-400 hover:border-zinc-600")
                  }
                >
                  {form.active ? <Eye size={12} /> : <EyeOff size={12} />}
                  {form.active ? "Live" : "Hidden"}
                </button>
              </div>
            </div>

            {/* Preview link */}
            {form.affiliate_url && (
              <a
                href={form.affiliate_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#D4AF37] mb-4"
              >
                <ExternalLink size={10} /> Preview affiliate link
              </a>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setForm(null)}
                disabled={saving}
                data-testid="form-cancel"
                className="flex-1 border border-zinc-700 hover:border-white text-white font-bold uppercase tracking-widest text-xs py-3 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="form-save"
                className="flex-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 transition-all disabled:opacity-50"
              >
                {saving ? "Saving…" : form.partner_id ? "Update Partner" : "Add Partner"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .form-input {
          width: 100%;
          background: #0A0A0A;
          border: 1px solid #27272a;
          color: #ffffff;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s;
        }
        .form-input:focus { border-color: #D4AF37; }
      `}</style>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={
      "border p-3 " +
      (highlight ? "border-[#D4AF37]/50 bg-[#160B22]" : "border-zinc-900 bg-[#0F0F0F]")
    }>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={
        "font-mono tabular-nums text-2xl font-bold " +
        (highlight ? "text-[#D4AF37]" : "text-white")
      }>
        {value}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}


// 👨‍🍳 Owner panel — site-wide Chief Cook David Lauer egg drop trigger.
function PartnerEggAnnouncePanel() {
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const { data } = await axios.get(`${API}/partner-eggs/active`);
      const davids = (data?.active || []).filter((a) => a.key === "david_lauer");
      setActive(davids[0] || null);
    } catch {
      /* no-op */
    }
  }
  useEffect(() => { refresh(); }, []);

  async function announce() {
    if (busy) return;
    if (!window.confirm("Drop a Chief Cook David Lauer egg into EVERY user's nest and raise the banner for 24h? This cannot be undone.")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/partner-eggs/david_lauer/announce`);
      toast.success(`👨‍🍳 Dropped to ${data?.awarded_users || 0} users. Banner up for 24h.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Announce failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="partner-egg-panel"
      className="mb-8 bg-[#0F0F0F] border-2 p-5 flex items-center gap-5 flex-wrap"
      style={{ borderColor: "#FCD34D" }}
    >
      <div className="text-5xl">👨‍🍳🥚</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "#FCD34D" }}>
          Partner Egg Drop · A★
        </div>
        <div className="font-display text-xl text-white leading-tight">
          Chief Cook David Lauer Egg
        </div>
        <div className="text-xs text-neutral-400 mt-1">
          25 🪹 + 1 Phoenix Feather · 50% Phoenix Crown · 10% chance of a 24h VIP Pass on hatch.
        </div>
        {active ? (
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mt-2">
            Banner LIVE · {active.recipients_count || 0} users awarded · ends {new Date(active.banner_until).toLocaleString()}
          </div>
        ) : (
          <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-2">
            No active banner. Triggers a one-shot drop to all users + 24h site banner.
          </div>
        )}
      </div>
      <button
        onClick={announce}
        disabled={busy}
        data-testid="partner-egg-announce-btn"
        className="font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-colors disabled:opacity-50"
        style={{ background: "#FCD34D", color: "#0A0A0A", border: "1px solid #FCD34D" }}
      >
        {busy ? "Dropping…" : (active ? "Drop Again" : "Announce Drop")}
      </button>
    </div>
  );
}



// 👨‍🍳 Owner panel — edit chef bio / photo / tagline / social links.
function ChefBioEditorPanel({ slug = "david-lauer" }) {
  const [chef, setChef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/chefs/${slug}`);
      setChef(data?.chef || null);
      const c = data?.chef || {};
      setForm({
        name: c.name || "",
        title: c.title || "",
        tagline: c.tagline || "",
        bio: c.bio || "",
        photo_url: c.photo_url || "",
        social: {
          instagram: c.social?.instagram || "",
          tiktok: c.social?.tiktok || "",
          youtube: c.social?.youtube || "",
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load chef.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const updSocial = (k) => (e) =>
    setForm((s) => ({ ...s, social: { ...s.social, [k]: e.target.value } }));

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/chefs/${slug}`, form);
      setChef(data?.chef || null);
      toast.success("Chef profile updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !form) {
    return (
      <div className="mb-8 bg-[#0F0F0F] border border-zinc-900 p-5 text-neutral-500 text-xs">
        Loading chef…
      </div>
    );
  }

  return (
    <div
      data-testid="chef-bio-panel"
      className="mb-8 bg-[#0F0F0F] border-2 border-zinc-900 p-5"
    >
      <div className="flex items-center gap-4 flex-wrap mb-4">
        <div className="text-4xl">👨‍🍳</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
            Chef Profile Editor
          </div>
          <div className="font-display text-lg text-white leading-tight">
            {chef?.name || "Chief Cook"} · {chef?.follower_count || 0} followers · {chef?.recipe_count || 0} recipes
          </div>
        </div>
        <a
          href={`/chief-cook/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white"
        >
          View public page →
        </a>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Display Name">
          <input
            data-testid="chef-edit-name"
            className="input-omni" value={form.name} onChange={upd("name")}
          />
        </Field>
        <Field label="Title">
          <input
            data-testid="chef-edit-title"
            className="input-omni" value={form.title} onChange={upd("title")}
            placeholder="Chief Cook"
          />
        </Field>
        <Field label="Tagline (1 line)">
          <input
            data-testid="chef-edit-tagline"
            className="input-omni" value={form.tagline} onChange={upd("tagline")}
            placeholder="Flavor for the flock 🔥"
            maxLength={200}
          />
        </Field>
        <Field label="Photo URL (https://… or data: URL)">
          <input
            data-testid="chef-edit-photo"
            className="input-omni" value={form.photo_url} onChange={upd("photo_url")}
            placeholder="https://…"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Bio">
            <textarea
              data-testid="chef-edit-bio"
              className="input-omni h-32 resize-y" value={form.bio} onChange={upd("bio")}
              maxLength={2000}
              placeholder="Tell people about the chef…"
            />
          </Field>
          <div className="text-[10px] text-neutral-600 -mt-2 mb-2 text-right">
            {form.bio.length}/2000
          </div>
        </div>
        <Field label="Instagram (handle, no @)">
          <input
            data-testid="chef-edit-ig"
            className="input-omni" value={form.social.instagram} onChange={updSocial("instagram")}
            placeholder="chef_david"
          />
        </Field>
        <Field label="TikTok (handle, no @)">
          <input
            data-testid="chef-edit-tt"
            className="input-omni" value={form.social.tiktok} onChange={updSocial("tiktok")}
            placeholder="chef_david"
          />
        </Field>
        <Field label="YouTube (channel handle or full URL)">
          <input
            data-testid="chef-edit-yt"
            className="input-omni" value={form.social.youtube} onChange={updSocial("youtube")}
            placeholder="@ChefDavid"
          />
        </Field>
      </div>
      <button
        data-testid="chef-edit-save"
        onClick={save}
        disabled={saving}
        className="mt-4 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-colors disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Chef Profile"}
      </button>
    </div>
  );
}
